import { Fingerprint, ShieldCheck, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "./api";
import { useAuth } from "./auth";
import { isMobilePlatform, passkeyErrorMessage, passkeysAvailable, registerPasskey, type PasskeyStatus } from "./passkeys";

const promptKey = (userId: string) => `ja:passkey-prompt:${userId}`;
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

export function PasskeyEnrollmentPrompt() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const activateButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!user || !isMobilePlatform()) return;
    const snoozedUntil = Number(localStorage.getItem(promptKey(user.userId)));
    if (Number.isFinite(snoozedUntil) && snoozedUntil > Date.now()) return;
    let active = true;
    const timer = window.setTimeout(() => {
      void Promise.all([passkeysAvailable(), api<PasskeyStatus>("/auth/passkeys/status")])
        .then(([available, status]) => { if (active && available && !status.registered) setOpen(true); })
        .catch(() => { /* A biometria é opcional e não bloqueia o app. */ });
    }, 900);
    return () => { active = false; window.clearTimeout(timer); };
  }, [user]);

  useEffect(() => {
    if (!open) return;
    activateButton.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) {
        localStorage.setItem(promptKey(user!.userId), String(Date.now() + SNOOZE_MS));
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [busy, open, user]);

  if (!open || !user) return null;

  const dismiss = () => {
    localStorage.setItem(promptKey(user.userId), String(Date.now() + SNOOZE_MS));
    setOpen(false);
  };
  const activate = async () => {
    setBusy(true); setError("");
    try {
      await registerPasskey();
      localStorage.removeItem(promptKey(user.userId));
      setOpen(false);
    } catch (caught) { setError(passkeyErrorMessage(caught, "Não foi possível ativar a biometria.")); }
    finally { setBusy(false); }
  };

  return <div className="passkey-prompt-backdrop" role="presentation">
    <section className="passkey-prompt" role="dialog" aria-modal="true" aria-labelledby="passkey-prompt-title">
      <button type="button" className="icon-close" onClick={dismiss} aria-label="Agora não"><X size={18} /></button>
      <span className="passkey-prompt-icon"><Fingerprint size={28} /></span>
      <div><span className="site-eyebrow">Acesso rápido</span><h2 id="passkey-prompt-title">Ativar biometria?</h2></div>
      <div className="passkey-security-note"><ShieldCheck size={17} /><span>A biometria não é enviada à JA. O aparelho confirma sua identidade com uma passkey protegida.</span></div>
      {error && <p className="form-error" role="alert">{error}</p>}
      <div className="passkey-prompt-actions"><button type="button" className="site-button site-button-outline" onClick={dismiss} disabled={busy}>Agora não</button><button ref={activateButton} type="button" className="site-button site-button-red" onClick={() => void activate()} disabled={busy} aria-busy={busy}><Fingerprint size={17} />{busy ? "Ativando…" : "Ativar biometria"}</button></div>
    </section>
  </div>;
}
